import {
  AnimatedModal,
  Close,
  EModalVariants,
  ETextStyleVariant,
  Modal,
  Overlay,
  Text,
  useModalContext,
} from '@outdoorsyco/bonfire';
import { useRef } from 'react';
import { FocusScope } from 'react-aria';
import { useIntl } from 'react-intl';
import { useMenuTriggerState } from 'react-stately';

import { useBreakpoint } from '@/hooks/useBreakpoint';

import { GlobalHeaderMenuProvider } from './GlobalHeaderMenuContext';
import { GlobalHeaderMenuItems } from './GlobalHeaderMenuItems';
import { GlobalHeaderMenuTrigger } from './GlobalHeaderMenuTrigger';

type TGlobalHeaderMenuProps = {
  showBurgerMenuIcon?: boolean;
};

export const GlobalHeaderMenu = ({ showBurgerMenuIcon = true }: TGlobalHeaderMenuProps) => {
  const intl = useIntl();

  const { isAboveDesktop } = useBreakpoint();

  const triggerRef = useRef<HTMLButtonElement>(null);
  const overlayState = useMenuTriggerState({});

  return (
    <GlobalHeaderMenuProvider onClose={() => overlayState.close()}>
      <div className="relative">
        <GlobalHeaderMenuTrigger
          ref={triggerRef}
          aria-label={intl.formatMessage({
            id: 'PkA1lb',
            defaultMessage: 'Open menu',
            description: 'GlobalHeader > Account button aria-label',
          })}
          onClick={() => overlayState.toggle()}
          showBurgerMenuIcon={showBurgerMenuIcon}
          isOpen={overlayState.isOpen}
        />

        {isAboveDesktop ? (
          overlayState.isOpen && (
            <Overlay
              triggerRef={triggerRef}
              triggerState={overlayState}
              popperProps={{ placement: 'bottom-end' }}
              onDismiss={() => overlayState.close()}>
              <FocusScope
                // Moving focus into an overlay on open is required for keyboard accessibility
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                restoreFocus
                contain>
                <div className="py-2 mt-2 bg-white border border-gray-200 rounded-2xl shadow-dropdown min-w-80">
                  <GlobalHeaderMenuItems />
                </div>
              </FocusScope>
            </Overlay>
          )
        ) : (
          <AnimatedModal
            isOpen={overlayState.isOpen}
            onClose={() => overlayState.close()}
            variant={EModalVariants.Right}
            withOverlay
            isDismissable
            dialogClassName="rounded-l-none">
            <Modal.Header className="flex items-center justify-between px-5 py-10">
              <Text variant={ETextStyleVariant.LargeBold}>
                {intl.formatMessage({
                  id: 'X/R37b',
                  defaultMessage: 'Menu',
                  description: 'GlobalHeader > Mobile menu > Header title',
                })}
              </Text>

              <ModalCloseButton />
            </Modal.Header>
            <Modal.Content className="p-0">
              <GlobalHeaderMenuItems />
            </Modal.Content>
          </AnimatedModal>
        )}
      </div>
    </GlobalHeaderMenuProvider>
  );
};

const ModalCloseButton = () => {
  const { onClose } = useModalContext();
  return <Close onClose={onClose} />;
};
